import classnames from 'classnames'
import styles from './ProductPageUiInfoTable.module.scss'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import X from '@/components/_svg/X'
import useStore from '@/store'
import Link from '@/components/Link/Link'

const ProductPageUiInfoTable = forwardRef(
  // eslint-disable-next-line no-unused-vars
  ({ className, productData }, ref) => {
    const textLinesRefs = useRef({
      productTitle: null,
      productStyleLabel: null,
      productStyleValue: null,
      productVariantLabel: null,
      productVariantValue: null,
      productDimensionsLabel: null,
      productDimensionsValue: null,
      productComColLabel: null,
      productComColValue: null,
      productFinishLabel: null,
      productFinishValue: null,
    })

    const lineRefs = useRef({
      productStyle: null,
      productVariant: null,
      productDimensions: null,
      productComCol: null,
      productFinish: null,
    })

    const tableRowRefs = useRef({
      productStyle: null,
      productVariant: null,
      productDimensions: null,
      productComCol: null,
      productFinish: null,
    })

    const globalData = useStore(state => state.globalData)
    const navigationData = globalData?.navigation
    const variants = productData?.productData?.variants || []
    const [activeVariant, setActiveVariant] = useState(!!variants?.length ? variants[0] : null)
    const hasVariants = productData.productData.variants?.length
    const $inquireButtonRef = useRef(null)
    const materialsByCategory = useMemo(() => {
      if (!productData?.productData?.variants?.length) return {}
      const materialCategories = {}
      productData?.productData?.materialOptions?.forEach(material => {
        if (!material) return
        const { materialCategory, title } = material
        const materialCategoryTitle = materialCategory.title
        if (!materialCategories[materialCategoryTitle]) {
          materialCategories[materialCategoryTitle] = []
        }
        if (!materialCategories[materialCategoryTitle].includes(title)) {
          materialCategories[materialCategoryTitle].push(title)
        }
      })
      return materialCategories
    }, [productData])
    const itemsToShow = useMemo(() => {
      const contentToShow = []

      if (productData?.productData?.variants?.length > 1) {
        contentToShow.push('title')
      }

      let showDimensions = false
      productData?.productData?.variants.forEach(variant => {
        if (variant?.dimensions) {
          showDimensions = true
        }
      })
      if (showDimensions) {
        contentToShow.push('dimensions')
      }

      let showComCol = false
      productData?.productData?.variants.forEach(variant => {
        if (variant?.comcol) {
          showComCol = true
        }
      })
      if (showComCol) {
        contentToShow.push('comcol')
      }

      return contentToShow
    }, [productData?.productData])

    useEffect(() => {
      if (!hasVariants) return
      const activeVariant = productData?.productData?.variants[0]
      if (activeVariant) {
        setActiveVariant(activeVariant)
      }
    }, [productData?.productData?.variants, hasVariants])

    useImperativeHandle(ref, () => ({
      animateIn: () => {
        Object.values(lineRefs.current).forEach(($el, i) => {
          if (!$el) return
          gsap.killTweensOf($el)
          gsap.set($el, {
            transformOrigin: 'left',
          })
          gsap.to($el, {
            duration: 0.8,
            ease: 'Power3.easeOut',
            scaleX: 1,
            delay: i * 0.07,
          })
        })
        Object.values(textLinesRefs.current).forEach($ref => {
          if ($ref) $ref.animate()
        })
        Object.values(tableRowRefs.current).forEach($ref => {
          if ($ref) $ref.animate(true)
        })

        if ($inquireButtonRef.current) {
          gsap.killTweensOf($inquireButtonRef.current)
          gsap.to($inquireButtonRef.current, {
            y: 0,
            duration: 1.2,
            ease: 'Power4.easeOut',
          })
        }
      },
      animateOut: () => {
        Object.values(lineRefs.current).forEach($el => {
          if (!$el) return
          gsap.killTweensOf($el)
          gsap.set($el, {
            transformOrigin: 'right',
          })
          gsap.to($el, {
            duration: 0.6,
            ease: 'Power3.easeOut',
            scaleX: 0,
          })
        })
        Object.values(textLinesRefs.current).forEach($ref => {
          if ($ref)
            $ref.animateOut({
              stagger: 0,
              onComplete: () => {
                setActiveVariant(null)
              },
            })
        })
        Object.values(tableRowRefs.current).forEach($ref => {
          if ($ref) $ref.animate(false)
          if ($ref) {
            $ref.setDropdownIsOpen(false)
          }
        })

        if ($inquireButtonRef.current) {
          gsap.killTweensOf($inquireButtonRef.current)
          gsap.to($inquireButtonRef.current, {
            y: '-105%',
            duration: 0.6,
            ease: 'Power3.easeOut',
          })
        }
      },
      reset: () => {
        Object.values(textLinesRefs.current).forEach($ref => {
          if ($ref) $ref.reset()
        })
        if ($inquireButtonRef.current) {
          gsap.killTweensOf($inquireButtonRef.current)
          gsap.set($inquireButtonRef.current, {
            y: '105%',
          })
        }
      },
    }))

    if (!productData) return null

    return (
      <div className={classnames(styles.ProductPageUiInfoTable, className)}>
        {/* Product Title */}
        <div className={styles.productTitleContainer}>
          <AnimatedTextLines
            querySelector="span"
            ref={ref => {
              textLinesRefs.current.productTitle = ref
            }}
            autoAnimate={false}
            className={classnames(styles.productTitle__lines, styles.textLines)}
          >
            <span className={classnames(styles.productTitle, styles.textLinesValue)}>{productData.title}</span>
          </AnimatedTextLines>
        </div>
        <TableRow
          ref={ref => {
            if (ref) {
              textLinesRefs.current.productStyleLabel = ref.label
              textLinesRefs.current.productStyleValue = ref.value
              lineRefs.current.productStyle = ref.line
              tableRowRefs.current.productStyle = ref
            }
          }}
          label="Style"
          value={productData.productData.style}
        />
        <TableRow
          isHidden={!itemsToShow.includes('title')}
          ref={ref => {
            if (ref) {
              textLinesRefs.current.productVariantLabel = ref.label
              textLinesRefs.current.productVariantValue = ref.value
              lineRefs.current.productVariant = ref.line
              tableRowRefs.current.productVariant = ref
            }
          }}
          label="Size"
          value={hasVariants ? productData?.productData?.variants[0]?.title : 'NA'}
          content={
            hasVariants ? (
              <ul
                className={styles.variantsList}
                key={productData.slug.current}
              >
                {productData.productData.variants.map((variant, i) => {
                  if (variant?.title === activeVariant?.title) return null

                  return (
                    <li
                      className={styles.variantsList__item}
                      key={`${variant.title}-${i}-${productData.slug.current}`}
                    >
                      <button
                        className={styles.variantsList__itemButton}
                        ariaLabel={`Select ${variant.title} size`}
                        onClick={() => {
                          textLinesRefs.current.productVariantValue.updateText(variant.title)
                          textLinesRefs.current.productDimensionsValue.updateText(variant.dimensions)
                          textLinesRefs.current.productComColValue.updateText(variant.comcol)
                          tableRowRefs.current.productVariant.setDropdownIsOpen(false)
                          setActiveVariant(variant)
                        }}
                      >
                        {variant.title}
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : null
          }
        />
        <TableRow
          isHidden={!itemsToShow.includes('dimensions')}
          ref={ref => {
            if (ref) {
              textLinesRefs.current.productDimensionsLabel = ref.label
              textLinesRefs.current.productDimensionsValue = ref.value
              lineRefs.current.productDimensions = ref.line
              tableRowRefs.current.productDimensions = ref
            }
          }}
          label="Dimensions"
          value={hasVariants ? productData?.productData?.variants[0]?.dimensions : 'NA'}
        />
        <TableRow
          isHidden={!itemsToShow.includes('comcol')}
          ref={ref => {
            if (ref) {
              textLinesRefs.current.productComColLabel = ref.label
              textLinesRefs.current.productComColValue = ref.value
              lineRefs.current.productComCol = ref.line
              tableRowRefs.current.productComCol = ref
            }
          }}
          label="COM / COL"
          value={hasVariants ? productData?.productData?.variants[0]?.comcol : 'NA'}
        />
        <TableRow
          isHidden={!Object.keys(materialsByCategory).length}
          ref={ref => {
            if (ref) {
              textLinesRefs.current.productFinishLabel = ref.label
              textLinesRefs.current.productFinishValue = ref.value
              lineRefs.current.productFinish = ref.line
              tableRowRefs.current.productFinish = ref
            }
          }}
          label="Materials"
          value="Options"
          content={
            Object.keys(materialsByCategory).length > 0 ? (
              <div
                className={styles.specificationOptionsGrid}
                key={`Finishes-${productData.slug.current}`}
              >
                {Object.keys(materialsByCategory).map(category => {
                  return (
                    <div
                      className={styles.specificationOptionsGrid__item}
                      key={`${category}-${productData.slug.current}`}
                    >
                      <h3 className={styles.specificationOptionsGrid__itemTitle}>{category} Options</h3>
                      <p className={styles.specificationOptionsGrid__items}>
                        {materialsByCategory[category].map((material, i) => {
                          return (
                            <span key={`${material}-${i}-${category}`}>
                              {material}
                              {i !== materialsByCategory[category].length - 1 && <span>,&nbsp;</span>}
                            </span>
                          )
                        })}
                      </p>
                    </div>
                  )
                })}
              </div>
            ) : null
          }
        />
        <div className={styles.inquireContainer}>
          <Link
            link={{
              linkType: 'external',
              link: `mailto:${navigationData?.email}?subject=Inquiry: ${productData.title}`,
            }}
            className={styles.inquireContainerLink}
          >
            <div
              className={styles.inquireButton}
              ref={$inquireButtonRef}
            >
              <span className={styles.inquireButton__text}>Inquire About Product</span>
              <span className={styles.inquireButton__bgHover} />
            </div>
          </Link>
        </div>
      </div>
    )
  },
)

ProductPageUiInfoTable.displayName = 'ProductPageUiInfoTable'

const TableRow = forwardRef(({ label, value, content, isHidden = false }, ref) => {
  const textLinesRefs = useRef({
    label: null,
    value: null,
  })
  const lineRef = useRef(null)
  const $contentRef = useRef(null)
  const $contentInnerRef = useRef(null)
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
  const ValueElement = content ? 'button' : 'span'
  const $plusMinusRef = useRef(null)

  const animateDropdown = (open = true) => {
    if (!$contentRef.current || !$contentInnerRef.current || !$plusMinusRef.current) return
    gsap.killTweensOf([$contentRef.current, $plusMinusRef.current], 'height,y,rotate')
    const height = $contentInnerRef.current.offsetHeight
    gsap.to($contentRef.current, {
      height: open ? height : 0,
      duration: open ? 0.6 : 0.3,
      ease: 'Power3.easeOut',
    })
    gsap.to($plusMinusRef.current, {
      rotate: !open ? 45 : 0,
      duration: !open ? 0.6 : 0.3,
      y: !open ? '-55%' : '10%',
      ease: 'Power3.easeOut',
    })
  }

  const animate = (animateIn = true) => {
    if ($plusMinusRef.current) {
      gsap.killTweensOf($plusMinusRef.current)
      gsap.to($plusMinusRef.current, {
        opacity: animateIn ? 1 : 0,
        duration: animateIn ? 0.6 : 0.3,
        ease: 'Power3.easeOut',
      })
    }
  }

  useEffect(() => {
    if (content) {
      animateDropdown(dropdownIsOpen)
    }
  }, [dropdownIsOpen, content])

  useImperativeHandle(ref, () => ({
    label: textLinesRefs.current.label,
    value: textLinesRefs.current.value,
    line: lineRef.current,
    setDropdownIsOpen,
    animate,
  }))

  return (
    <div
      className={classnames(
        styles.tableRow,
        { [styles.hidden]: isHidden },
        {
          [styles.dropdownIsOpen]: dropdownIsOpen,
        },
      )}
    >
      <div className={styles.tableRow__header}>
        <span className={styles.tableRowLabel}>
          <AnimatedTextLines
            querySelector="span"
            ref={ref => {
              textLinesRefs.current.label = ref
            }}
            autoAnimate={false}
            className={classnames(styles.tableRowLabel__lines, styles.textLines)}
          >
            <span className={classnames(styles.tableRowLabel__label, styles.textLinesValue)}>{label}</span>
          </AnimatedTextLines>
        </span>
        <ValueElement
          ariaLabel={`View ${label} options`}
          className={styles.tableRowValue}
          onClick={() => {
            if (!content) return
            setDropdownIsOpen(prev => !prev)
          }}
        >
          <AnimatedTextLines
            querySelector="span"
            ref={ref => {
              textLinesRefs.current.value = ref
            }}
            autoAnimate={false}
            className={classnames(styles.tableRowValue__lines, styles.textLines)}
          >
            <span className={classnames(styles.tableRowValue__label, styles.textLinesValue)}>{value}</span>
          </AnimatedTextLines>
          {content && (
            <span
              className={styles.tableRowValue__plusMinus}
              ref={$plusMinusRef}
            >
              <X />
            </span>
          )}
        </ValueElement>
      </div>
      {content && (
        <div
          className={styles.tableRowContent}
          ref={$contentRef}
        >
          <div
            className={styles.tableRowContent__inner}
            ref={$contentInnerRef}
          >
            {content}
          </div>
        </div>
      )}
      <div
        className={styles.tableRowLine}
        ref={lineRef}
      />
    </div>
  )
})

TableRow.displayName = 'TableRow'

export default ProductPageUiInfoTable
